import React, { useRef, useState } from "react";
import PageAlert from "../../../components/PageAlert/PageAlert";
import { resolveApiError } from "../../../utils/apiError";
import MediaLibraryModal from "./MediaLibraryModal";
import {
  getArticleMediaUploadError,
  mapMediaToImageData,
  uploadArticleMedia,
  validateArticleMediaFile,
} from "../articleApi";

const MediaControl = ({
  value = {},
  onChange,
  articleId = null,
  showFields = true,
  emptyTitle = "No image selected",
  emptyHint = "Upload an image or select one from the media library.",
  libraryTitle = "Media Library",
  libraryDescription = "Select an image for this block.",
}) => {
  const fileInputRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const imageUrl = value.preview_url || value.url || "";

  const updateValue = (key, nextValue) => {
    onChange({ ...value, [key]: nextValue });
  };

  const applyMedia = (media, extra = {}) => {
    const mapped = mapMediaToImageData(media);
    onChange({
      ...value,
      ...mapped,
      ...extra,
      url: mapped.url,
      alt: extra.alt || mapped.alt,
      caption: extra.caption || mapped.caption,
      preview_url: mapped.preview_url,
      media_id: media.id ?? null,
    });
  };

  const handleFileChange = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;

    const invalidMessage = validateArticleMediaFile(file);
    if (invalidMessage) {
      setError(invalidMessage);
      input.value = "";
      return;
    }

    setIsUploading(true);
    setError("");

    try {
      const media = await uploadArticleMedia({
        file,
        article_id: articleId || null,
        alt_text: value.alt || file.name,
        caption: value.caption || "",
      });
      applyMedia(media, { alt: value.alt || media.alt_text || "", caption: value.caption || media.caption || "" });
    } catch (err) {
      const uploadError = getArticleMediaUploadError(err);
      if (uploadError) setError(uploadError);
      else resolveApiError(err, setError, undefined);
    } finally {
      setIsUploading(false);
      input.value = "";
    }
  };

  const handleSelectMedia = (media) => {
    applyMedia(media);
    setIsModalOpen(false);
  };

  const removeImage = () => {
    onChange({
      ...value,
      url: "",
      alt: showFields ? "" : value.alt || "",
      caption: showFields ? "" : value.caption || "",
      preview_url: "",
      media_id: null,
      file: null,
    });
  };

  return (
    <div className={`media-control${showFields ? "" : " media-control--compact"}`}>
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" hidden onChange={handleFileChange} />

      {!imageUrl && (
        <div className="media-control__empty">
          {emptyTitle ? <strong>{emptyTitle}</strong> : null}
          {emptyHint ? <span>{emptyHint}</span> : null}
          <div className="media-control__actions">
            <button type="button" onClick={() => fileInputRef.current?.click()}>Upload</button>
            <button type="button" onClick={() => setIsModalOpen(true)}>Media Library</button>
          </div>
        </div>
      )}

      {imageUrl && (
        <div className="media-control__selected">
          <div className="media-control__preview">
            <img src={imageUrl} alt={value.alt || ""} />
          </div>
          <div className="media-control__actions">
            <button type="button" onClick={() => fileInputRef.current?.click()}>Replace</button>
            <button type="button" onClick={() => setIsModalOpen(true)}>Media Library</button>
            <button type="button" className="is-danger" onClick={removeImage}>Remove</button>
          </div>
        </div>
      )}

      {isUploading && <div className="media-control__uploading">Uploading image...</div>}
      {error && <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>}

      {showFields && (
        <div className="media-control__fields">
          <label>
            Alt text
            <input
              value={value.alt || ""}
              placeholder="Describe this image"
              onChange={(event) => updateValue("alt", event.target.value)}
            />
          </label>
          <label>
            Caption
            <textarea
              rows={3}
              value={value.caption || ""}
              placeholder="Image caption"
              onChange={(event) => updateValue("caption", event.target.value)}
            />
          </label>
        </div>
      )}

      {isModalOpen && (
        <MediaLibraryModal
          title={libraryTitle}
          description={libraryDescription}
          imagesOnly
          onClose={() => setIsModalOpen(false)}
          onSelect={handleSelectMedia}
        />
      )}
    </div>
  );
};

export default MediaControl;
